import { useCallback, useEffect, useRef, useState } from "react"
import { usePartialState } from "./use-partial-state"
import { errorWrapInHandler } from "../services/utils/errors.service"
import { useDebouncer } from "./use-debouncer"
import { useNonConcurrentPromiseProxy } from "./use-non-concurrent-promise-proxy"

export interface BaseQuery {
  'q[id_not_in][]'?: number[]
}

export interface BaseEntity {
  id: number
}

interface PaginationParams<Entity extends BaseEntity, Query extends BaseQuery> {
  defaultQuery?: Query
  loader: (query: Query) => Promise<{ entities: Entity[], hasMore: boolean }>
  afterLoad?: (query: Query) => void | Promise<void>
  loaderDeps?: any[]
  setErrors: (errors: string[]) => void
  debounceLatency?: number
}

export function usePagination<Entity extends BaseEntity, Query extends BaseQuery> ({ defaultQuery, loaderDeps, debounceLatency = 1000, loader, afterLoad, setErrors }: PaginationParams<BaseEntity, Query>) {
  const [entities, setEntities] = useState<Entity[]>([])
  const [query, setQueryState, setQuery] = usePartialState(defaultQuery || {} as Query)
  const [isLoading, setLoading] = useState(true)
  const [hasMore, setHasMore] = useState(false)
  const [anyResultBeforeFilter, setAnyResultBeforeFilter] = useState(false)
  const loadConcurrentProxy = useNonConcurrentPromiseProxy()
  const debouncer = useDebouncer(debounceLatency)

  const load = useCallback(async (loadMore = false, currentQuery = query) => {
    return errorWrapInHandler(async () => {
      setLoading(true)
      if (currentQuery !== query) setQuery(currentQuery)

      const newQuery = { ...currentQuery }
      if (loadMore) newQuery['q[id_not_in][]'] = entities.map(_ => _.id)
      else setEntities([]) // avoid showing stall entities if not loading more entities

      const res = await loadConcurrentProxy(loader(newQuery))
      if (loadMore) res.entities.unshift(...entities)
      setEntities(res.entities as Entity[])
      setLoading(false)
      setHasMore(res.hasMore)
      setAnyResultBeforeFilter(anyResultBeforeFilter || res.entities.length > 0)
      if (afterLoad && !loadMore) await afterLoad(currentQuery)
      return true
    }, setErrors)()
  }, [afterLoad, anyResultBeforeFilter, entities, loadConcurrentProxy, loader, query, setErrors, setQuery])

  // store the load function so the debouced version can access it (instead of the one from the first render)
  const loadRef = useRef(load)
  useEffect(() => { loadRef.current = load }, [load])

  // load the entities on component mount (and if loaderDeps is set, on every change of it)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load() }, loaderDeps || [])

  const setQueryStateDebouncedLoad = useCallback((body: Partial<Query>) => {
    // set the query state and skip the load via useEffect by overriding the previous query state
    const newQuery = { ...query, ...body }
    setQueryState(newQuery)

    debouncer(() => void loadRef.current())
  }, [query, setQueryState, debouncer])

  const setQueryStateLoad = useCallback(async (body: Partial<Query>) => {
    const newQuery = { ...query, ...body }
    await load(false, newQuery)
  }, [load, query])

  const resetQueryLoad = useCallback(async (useDefaultQuery = true) => {
    await load(false, (useDefaultQuery ? defaultQuery : null) || {} as Query)
  }, [defaultQuery, load])

  const loadMoreAll = useCallback(async () => {
    let hasMoreLocal = hasMore, entitiesLocal = entities
    setLoading(true)
    
    while (hasMoreLocal) {
      const newQuery = { ...query, 'q[id_not_in][]': entitiesLocal.map(_ => _.id) }
      const res = await loadConcurrentProxy(loader(newQuery))
      hasMoreLocal = res.hasMore
      entitiesLocal = [...entitiesLocal, ...res.entities] as typeof entities
      setHasMore(hasMoreLocal)
      setEntities(entitiesLocal)
    }

    setLoading(false)
    return entitiesLocal
  }, [entities, hasMore, loadConcurrentProxy, loader, query])

  const reload = useCallback(async () => {
    let hasMoreLocal = true, entitiesLocal: typeof entities = []
    setLoading(true)
    setEntities([]) // avoid showing stall entities
    
    while (hasMoreLocal && entitiesLocal.length <= entities.length) {
      const newQuery = { ...query, 'q[id_not_in][]': entitiesLocal.map(_ => _.id) }
      const res = await loadConcurrentProxy(loader(newQuery))
      hasMoreLocal = res.hasMore
      entitiesLocal = [...entitiesLocal, ...res.entities] as typeof entities
      setHasMore(hasMoreLocal)
      setEntities(entitiesLocal)
    }

    setLoading(false)
    return entitiesLocal
  }, [entities.length, loadConcurrentProxy, loader, query])

  return { entities, query, isLoading, hasMore, anyResultBeforeFilter, setEntities, setQueryState, setQuery, setQueryStateDebouncedLoad, setQueryStateLoad, load, resetQueryLoad, loadMoreAll, reload }
}
